import { ColDef, GridOptions } from 'ag-grid-community'
import { PendingTradingEntityDetail } from 'api'
import { Locale, Translation } from 'locale'
import { gridOptionsBase, StatusCellRenderer, TypedValueGetterParams, ValidationCellRenderer } from 'view/components/datagrid'
import { booleanFormatter, getEntityStatusActiveSuspendedText, getEntityStatusColor, tradingEntityTypesFormatter } from '../datagrid'
import * as constants from 'view/styles/constants'

enum PendingTradingEntityGridColumnId {
  TRADING_ENTITY_ORGANISATION = 'TRADING_ENTITY_ORGANISATION',
  TRADING_ENTITY_PUBLISHED_NAME = 'TRADING_ENTITY_PUBLISHED_NAME',
  TRADING_ENTITY_SHORT_NAME = 'TRADING_ENTITY_SHORT_NAME',
  TRADING_ENTITY_FICC_SHORT_NAME = 'TRADING_ENTITY_FICC_SHORT_NAME',
  TRADING_ENTITY_STATUS = 'TRADING_ENTITY_STATUS',
  TRADING_ENTITY_LEI = 'TRADING_ENTITY_LEI',
  TRADING_ENTITY_BIC = 'TRADING_ENTITY_BIC',
  TRADING_ENTITY_TRADING_TYPE = 'TRADING_ENTITY_TRADING_TYPE',
  TC_EU_GFID = 'TC_EU_GFID',
  TC_US_GFID = 'TC_US_GFID',
  TC_15A6_ELIGIBLE = 'TC_15A6_ELIGIBLE',
  TC_COMBINED_EU_US_EOD_FILE = 'TC_COMBINED_EU_US_EOD_FILE',
  TC_CENTRAL_BANK = 'TC_CENTRAL_BANK',
  TC_NEGOTIATION_HISTORY_FILE = 'TC_NEGOTIATION_HISTORY_FILE',
  TC_OPERATING_COUNTRY = 'TC_OPERATING_COUNTRY'
}

function getTradingStatusColor(tradingEntityDetail: PendingTradingEntityDetail) {
  if (tradingEntityDetail.validationIssues && tradingEntityDetail.validationIssues.length > 0) {
    return constants.STATUS_COLOR_INVALID
  } else {
    return getEntityStatusColor(tradingEntityDetail.tradingEntity.status)
  }
}

export const PendingTradingEntityColumnDefs: ColDef[] = [
  {
    colId: PendingTradingEntityGridColumnId.TRADING_ENTITY_ORGANISATION,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.organisation?.shortCode,
    headerName: Locale.translate(Translation.TC_ORGANISATION),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.organisationId'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TRADING_ENTITY_PUBLISHED_NAME,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity?.publishedName,
    headerName: Locale.translate(Translation.TC_PUBLISHED_NAME),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.publishedName'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TRADING_ENTITY_SHORT_NAME,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity?.shortName,
    headerName: Locale.translate(Translation.TC_SHORT_NAME),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.shortName'
    }
  },

  {
    colId: PendingTradingEntityGridColumnId.TRADING_ENTITY_FICC_SHORT_NAME,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity?.ficcShortName,
    headerName: Locale.translate(Translation.SC_FICC_SHORT_NAME),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.ficcShortName'
    }
  },

  {
    colId: PendingTradingEntityGridColumnId.TRADING_ENTITY_STATUS,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) =>
      params.data && getEntityStatusActiveSuspendedText(params.data.tradingEntity.status),
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 120,
    equals: () => false,
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: (data: PendingTradingEntityDetail) => getTradingStatusColor(data)
    },
    lockVisible: true,
    lockPosition: true
  },
  {
    colId: PendingTradingEntityGridColumnId.TRADING_ENTITY_LEI,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity?.lei,
    headerName: Locale.translate(Translation.TC_LEI),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.lei'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TRADING_ENTITY_BIC,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity?.bic,
    headerName: Locale.translate(Translation.TC_BIC),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.bic'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TRADING_ENTITY_TRADING_TYPE,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity.tradingEntityTypes,
    headerName: Locale.translate(Translation.TC_TRADING_TYPE),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.tradingEntityTypes'
    },
    valueFormatter: tradingEntityTypesFormatter
  },
  {
    colId: PendingTradingEntityGridColumnId.TC_EU_GFID,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity.euGfidCode,
    headerName: Locale.translate(Translation.TC_EU_GFID),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.euGfidCode'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TC_US_GFID,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity.usGfidCode,
    headerName: Locale.translate(Translation.TC_US_GFID),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.usGfidCode'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TC_15A6_ELIGIBLE,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity.eligible15a6,
    headerName: Locale.translate(Translation.TC_15A6_ELIGIBLE),
    width: 120,
    valueFormatter: booleanFormatter,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.eligible15a6'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TC_COMBINED_EU_US_EOD_FILE,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity.eligibleCombinedRecFile,
    headerName: Locale.translate(Translation.TC_COMBINED_EU_US_EOD_FILE),
    width: 120,
    valueFormatter: booleanFormatter,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.eligibleCombinedRecFile'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TC_CENTRAL_BANK,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity.centralBank,
    headerName: Locale.translate(Translation.TC_CENTRAL_BANK),
    width: 120,
    valueFormatter: booleanFormatter,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.centralBank'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TC_NEGOTIATION_HISTORY_FILE,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity.negotiationHistoryFile,
    headerName: Locale.translate(Translation.TC_NEGOTIATION_HISTORY_FILE),
    width: 120,
    valueFormatter: booleanFormatter,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.negotiationHistoryFile'
    }
  },
  {
    colId: PendingTradingEntityGridColumnId.TC_OPERATING_COUNTRY,
    valueGetter: (params: TypedValueGetterParams<PendingTradingEntityDetail>) => params.data?.tradingEntity.operatingCountry,
    headerName: Locale.translate(Translation.TC_OPERATING_COUNTRY),
    width: 120,
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellRendererParams: {
      validationField: 'tradingEntity.operatingCountry'
    }
  }
]

export const pendingTradingEntityGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.tradingEntity.id
}
